import { useState, useCallback, useRef, useEffect } from 'react';
import { 
  MapPin, 
  Users, 
  Settings, 
  Trophy, 
  Play, 
  ChevronLeft, 
  ChevronRight,
  Check,
  Wallet,
  Coins,
  UserPlus,
  User,
  UserCircle,
  Edit,
  Trash2,
  ArrowLeft,
  RefreshCw,
  Flag
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiError } from '../utils/apiError';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { CarouselSurface } from './carousel/CarouselSurface';
import { LoopingDeck, CarouselArrows, wrapCarouselIndex } from './carousel/LoopingDeck';

/**
 * Compact looping setup for a Skins game; only explicit edits auto-save.
 * Replaces the tabbed interface during setup phase
 * Steps: Course -> Players -> Settings -> Prize Pool -> Review & Start
 * All settings auto-save to backend as user navigates
 */
const SkinsSetupCarousel = ({
  event,
  onUpdateEvent,
  onAddPlayer,
  onRemovePlayer,
  onStartGame,
  onOpenCourseLookup,
  onBack,
  onExit = onBack,
  isOpen = true,
  suspended = false,
  onEditHandicap,
  loadError,
  onRetry,
  players = []
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [gameName, setGameName] = useState(event?.name || '');
  const [gameDate, setGameDate] = useState(event?.date || '');
  const savingRef = useRef(false);
  const audioContextRef = useRef(null);
  
  // Local state for settings - seed from event props
  const [holes, setHoles] = useState(event?.holes || 18);
  const [startingNine, setStartingNine] = useState(event?.starting_nine || 'front');
  const [useHandicaps, setUseHandicaps] = useState(event?.use_handicaps || false);
  const [selectedTee, setSelectedTee] = useState(event?.selected_tee || null);
  const [hasPrizePool, setHasPrizePool] = useState(
    event?.has_prize_pool !== undefined ? event.has_prize_pool : null
  );
  const [entryFeeType, setEntryFeeType] = useState(event?.entry_fee_type || 'wallet');
  const [entryFee, setEntryFee] = useState(event?.entry_fee || 20);

  // Get available tees from event course data
  const availableTees = Array.isArray(event?.tees) ? event.tees.filter(t => t && typeof t.name === 'string') : [];

  // Sync state when event changes
  useEffect(() => {
    if (event) {
      setHoles(event.holes || 18);
      setStartingNine(event.starting_nine || 'front');
      setUseHandicaps(event.use_handicaps || false);
      setSelectedTee(event.selected_tee || null);
      if (event.has_prize_pool !== undefined) {
        setHasPrizePool(event.has_prize_pool);
      }
      if (event.entry_fee_type) {
        setEntryFeeType(event.entry_fee_type);
      }
      if (event.entry_fee) {
        setEntryFee(event.entry_fee);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  // Steps configuration - add Tee Box step after Course
  const steps = [
    { id: 'game', title: 'Game', icon: Edit, color: 'green' },
    { id: 'course', title: 'Course', icon: MapPin, color: 'green' },
    { id: 'tees', title: 'Tee Box', icon: Flag, color: 'cyan' },
    { id: 'players', title: 'Players', icon: Users, color: 'blue' },
    { id: 'settings', title: 'Settings', icon: Settings, color: 'purple' },
    { id: 'prizepool', title: 'Prize Pool', icon: Trophy, color: 'amber' },
    { id: 'start', title: 'Start', icon: Play, color: 'emerald' }
  ];

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(250, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(60, audioContext.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {}
  }, []);

  // Auto-save settings to backend when they change
  const saveSettings = useCallback(async (updates) => {
    if (!onUpdateEvent || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
      await onUpdateEvent(updates);
    } catch (e) {
      setSaveError(apiError(e, 'Settings were not saved. Please retry.'));
      setHoles(event.holes || 18); setStartingNine(event.starting_nine || 'front');
      setUseHandicaps(!!event.use_handicaps); setSelectedTee(event.selected_tee || null);
      setHasPrizePool(event.has_prize_pool ?? null); setEntryFee(event.entry_fee || 20);
      setEntryFeeType(event.entry_fee_type || 'wallet');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [onUpdateEvent, event]);

  // Save holes setting
  const handleHolesChange = (newHoles) => {
    setHoles(newHoles);
    saveSettings({ holes: newHoles });
  };

  // Save starting nine
  const handleStartingNineChange = (newStartingNine) => {
    setStartingNine(newStartingNine);
    saveSettings({ starting_nine: newStartingNine });
  };

  // Save handicaps toggle
  const handleHandicapsChange = (newUseHandicaps) => {
    setUseHandicaps(newUseHandicaps);
    saveSettings({ use_handicaps: newUseHandicaps });
  };

  // Save prize pool settings
  const handlePrizePoolChange = (newHasPrizePool) => {
    setHasPrizePool(newHasPrizePool);
    if (newHasPrizePool) {
      saveSettings({ 
        has_prize_pool: true, 
        entry_fee: entryFee, 
        entry_fee_type: entryFeeType 
      });
    } else {
      saveSettings({ 
        has_prize_pool: false, 
        entry_fee: 0, 
        entry_fee_type: null 
      });
    }
  };

  // Save entry fee type
  const handleEntryFeeTypeChange = (newType) => {
    setEntryFeeType(newType);
    const newFee = newType === 'wallet' ? 20 : 100;
    setEntryFee(newFee);
    saveSettings({ entry_fee_type: newType, entry_fee: newFee });
  };

  // Save entry fee amount
  const handleEntryFeeChange = (newFee) => {
    setEntryFee(newFee);
    saveSettings({ entry_fee: newFee });
  };

  // Save tee selection
  const handleTeeChange = (teeName) => {
    setSelectedTee(teeName);
    // Find the selected tee to get hole yardages
    const tee = availableTees.find(t => t.name === teeName);
    if (tee) {
      saveSettings({ 
        selected_tee: teeName,
        hole_yardages: tee.hole_yardages || [],
        ...(tee.hole_pars?.length ? { hole_pars: tee.hole_pars } : {}),
        ...(tee.hole_handicaps?.length ? { hole_handicaps: tee.hole_handicaps } : {})
      });
    } else {
      saveSettings({ selected_tee: teeName });
    }
  };

  // Get tee color class based on name
  const getTeeColorClass = (teeName) => {
    const name = (teeName || '').toLowerCase();
    if (name.includes('blue') || name.includes('champion')) return 'bg-blue-500';
    if (name.includes('white') || name.includes('member')) return 'bg-white border-2 border-gray-300';
    if (name.includes('gold') || name.includes('yellow')) return 'bg-yellow-400';
    if (name.includes('red') || name.includes('forward')) return 'bg-red-500';
    if (name.includes('black') || name.includes('pro')) return 'bg-gray-900';
    if (name.includes('green')) return 'bg-green-500';
    return 'bg-gray-400';
  };

  // Navigate carousel
  const goToStep = useCallback((index) => {
    const next = wrapCarouselIndex(index, steps.length);
    if (next !== currentStep) {
      playSwoosh();
      setCurrentStep(next);
    }
  }, [currentStep, steps.length, playSwoosh]);

  // Handle start game
  const handleStartGame = async () => {
    if (steps[currentStep].id !== 'start' || savingRef.current || !['course', 'tees', 'players', 'prizepool'].every(isStepComplete)) return;
    savingRef.current = true; setSaving(true);
    try { await onStartGame?.(); }
    catch (err) { setSaveError(apiError(err, 'The game could not be started. Please try again.')); }
    finally { savingRef.current = false; setSaving(false); }
  };

  // Check if step is complete
  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 'game':
        return !!event?.name?.trim() && !!event?.date;
      case 'course':
        return !!event?.course_name;
      case 'tees':
        // Tee is complete if selected OR if no tees available (skip)
        return !!selectedTee || availableTees.length === 0;
      case 'players':
        return players.length >= 2;
      case 'settings':
        return true;
      case 'prizepool':
        return hasPrizePool !== null;
      case 'start':
        return false;
      default:
        return false;
    }
  };

  // Get colors - add cyan for tees
  const getGradient = (color) => {
    const gradients = {
      green: 'from-green-900/90 to-green-800/50',
      cyan: 'from-cyan-900/90 to-cyan-800/50',
      blue: 'from-blue-900/90 to-blue-800/50',
      amber: 'from-amber-900/90 to-amber-800/50',
      purple: 'from-purple-900/90 to-purple-800/50',
      emerald: 'from-emerald-900/90 to-emerald-800/50'
    };
    return gradients[color];
  };

  const getBorderColor = (color) => {
    const borders = {
      green: 'border-green-500/60',
      cyan: 'border-cyan-500/60',
      blue: 'border-blue-500/60',
      amber: 'border-amber-500/60',
      purple: 'border-purple-500/60',
      emerald: 'border-emerald-500/60'
    };
    return borders[color];
  };

  const getActiveBg = (color) => {
    const backgrounds = {
      green: 'bg-green-500 text-white',
      cyan: 'bg-cyan-500 text-white',
      blue: 'bg-blue-500 text-white',
      amber: 'bg-amber-500 text-white',
      purple: 'bg-purple-500 text-white',
      emerald: 'bg-emerald-500 text-white'
    };
    return backgrounds[color];
  };

  const getIconColor = (color) => {
    const colors = {
      green: 'text-green-400',
      cyan: 'text-cyan-400',
      blue: 'text-blue-400',
      amber: 'text-amber-400',
      purple: 'text-purple-400',
      emerald: 'text-emerald-400'
    };
    return colors[color];
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step.id) {
      case 'game':
        return <div className="p-4 space-y-4 text-white">
          <label htmlFor="skins-game-name" className="block text-sm">Game name</label>
          <Input id="skins-game-name" data-testid="skins-game-name" value={gameName} maxLength={120} onChange={e => setGameName(e.target.value)} className="bg-white text-black" />
          <label htmlFor="skins-game-date" className="block text-sm">Date</label>
          <Input id="skins-game-date" data-testid="skins-game-date" type="date" value={gameDate} onChange={e => setGameDate(e.target.value)} className="bg-white text-black" />
          <Button data-testid="skins-game-save" disabled={!gameName.trim() || !gameDate || (gameName === event?.name && gameDate === event?.date)} onClick={() => saveSettings({ name: gameName.trim(), date: gameDate })} className="bg-green-600 text-white">Save game details</Button>
        </div>;
      case 'course': {
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            {event?.course_name ? (
              <>
                <div className="w-14 h-14 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-3">
                  <Check className="w-10 h-10 text-green-400" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-2">{event.course_name}</h4>
                {event.hole_pars?.length > 0 && (
                  <p className="text-green-300 text-lg mb-4">
                    Par {event.hole_pars.reduce((a, b) => a + (parseInt(b) || 0), 0)}
                  </p>
                )}
                <Button 
                  variant="outline" 
                  onClick={onOpenCourseLookup}
                  className="border-white/30 text-white hover:bg-white/10"
                  data-testid="change-course-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Change Course
                </Button>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="w-10 h-10 text-gray-400" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-2">Select a Course</h4>
                <p className="text-gray-300 mb-6">Choose where you'll be playing</p>
                <Button 
                  onClick={onOpenCourseLookup}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
                  data-testid="select-course-btn"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  Find Course
                </Button>
              </>
            )}
          </div>
        );
      }

      case 'tees': {
        // If no course selected, prompt to select course first
        if (!event?.course_name) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-4">
                <Flag className="w-10 h-10 text-gray-400" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Select Course First</h4>
              <p className="text-gray-300 mb-4">Choose a course to see available tee boxes</p>
              <Button 
                  onClick={() => goToStep(steps.findIndex(step => step.id === 'course'))}
                data-testid="skins-go-to-course"
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Go to Course
              </Button>
            </div>
          );
        }

        // If no tees available from course data
        if (availableTees.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-20 h-20 mx-auto bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
                <Check className="w-10 h-10 text-cyan-400" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2">No Tee Data Available</h4>
              <p className="text-gray-300 mb-4">This course doesn't have tee box information</p>
              <p className="text-cyan-300 text-sm">You can continue to the next step</p>
            </div>
          );
        }

        // Show tee options
        return (
          <div className="h-full overflow-y-auto px-2 py-2">
            <div className="text-center mb-4">
              <h4 className="text-lg font-bold text-white mb-1">Select Your Tee Box</h4>
              <p className="text-gray-300 text-sm">Yardages based on selected tee</p>
            </div>

            <div className="space-y-3">
              {availableTees.map((tee, index) => {
                const isSelected = selectedTee === tee.name;
                return (
                  <button
                    key={index}
                    onClick={() => handleTeeChange(tee.name)}
                    className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                      isSelected
                        ? 'border-cyan-400 bg-cyan-500/30'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                    data-testid={`tee-option-${tee.name?.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {/* Tee color indicator */}
                    <div className={`w-8 h-8 rounded-full ${getTeeColorClass(tee.name)} flex-shrink-0`} />
                    
                    {/* Tee info */}
                    <div className="flex-1 text-left">
                      <p className={`font-bold ${isSelected ? 'text-cyan-300' : 'text-white'}`}>
                        {tee.name}
                      </p>
                      <div className="flex items-center gap-3 text-sm">
                        <span className={isSelected ? 'text-cyan-200' : 'text-gray-300'}>
                          {tee.total_yards?.toLocaleString() || '---'} yards
                        </span>
                        {tee.course_rating && (
                          <span className="text-gray-400">
                            Rating: {tee.course_rating}
                          </span>
                        )}
                        {tee.slope_rating && (
                          <span className="text-gray-400">
                            Slope: {tee.slope_rating}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Saving indicator */}
            {saving && (
              <div className="flex items-center justify-center gap-2 text-cyan-300 text-sm mt-4">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            )}
          </div>
        );
      }

      case 'players': {
        return (
          <div className="h-full overflow-y-auto px-2">
            {/* Player count header */}
            <div className="text-center mb-4">
              <h4 data-testid="skins-player-count" className="text-xl font-bold text-white">
                {players.length} Player{players.length !== 1 ? 's' : ''}
              </h4>
              <p className={`text-sm ${players.length < 2 ? 'text-red-300' : 'text-green-300'}`}>
                {players.length < 2 ? `Need ${2 - players.length} more to start` : 'Ready to play!'}
              </p>
            </div>

            {/* Add Player Button */}
            <Button 
              onClick={onAddPlayer}
              className="w-full mb-4 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="add-player-btn"
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Add Player
            </Button>

            {/* Player List */}
            <div className="space-y-2">
              {players.map((player) => (
                <div 
                  key={player.player_id}
                  data-testid={`skins-player-${player.player_id}`}
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    player.user_id ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      player.user_id ? 'bg-amber-500/30' : 'bg-white/20'
                    }`}>
                      {player.user_id ? (
                        <UserCircle className="w-5 h-5 text-amber-300" />
                      ) : (
                        <User className="w-5 h-5 text-gray-300" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-white break-words" data-testid={`skins-player-name-${player.player_id}`}>{player.name}</p>
                      {player.handicap !== null && player.handicap !== undefined && (
                        <p className="text-xs text-gray-400">HCP {player.handicap}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {player.user_id && (
                      <span className="text-xs bg-amber-500/30 text-amber-300 px-2 py-1 rounded">Linked</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemovePlayer && onRemovePlayer(player.player_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2"
                      data-testid={`remove-player-${player.player_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {players.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No players yet</p>
                <p className="text-sm">Add at least 2 players to start</p>
              </div>
            )}
          </div>
        );
      }

      case 'settings': {
        return (
          <div className="space-y-4 px-1 py-1">
            {/* Number of Holes */}
            <div>
              <Label className="text-white/80 text-sm mb-3 block font-medium">Number of Holes</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleHolesChange(9)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    holes === 9 
                      ? 'border-purple-400 bg-purple-500/30 text-white' 
                      : 'border-white/20 text-gray-300 hover:border-white/40'
                  }`}
                  data-testid="holes-9-btn"
                >
                  <span className="font-bold text-lg">9 Holes</span>
                </button>
                <button
                  onClick={() => handleHolesChange(18)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    holes === 18 
                      ? 'border-purple-400 bg-purple-500/30 text-white' 
                      : 'border-white/20 text-gray-300 hover:border-white/40'
                  }`}
                  data-testid="holes-18-btn"
                >
                  <span className="font-bold text-lg">18 Holes</span>
                </button>
              </div>
            </div>

            {/* Front/Back 9 - Only show for 9 holes */}
            {holes === 9 && (
              <div>
                <Label className="text-white/80 text-sm mb-3 block font-medium">Which Nine?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleStartingNineChange('front')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      startingNine === 'front' 
                        ? 'border-purple-400 bg-purple-500/30 text-white' 
                        : 'border-white/20 text-gray-300 hover:border-white/40'
                    }`}
                    data-testid="front-9-btn"
                  >
                    <span className="font-medium">Front 9</span>
                    <span className="block text-xs opacity-70">Holes 1-9</span>
                  </button>
                  <button
                    onClick={() => handleStartingNineChange('back')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      startingNine === 'back' 
                        ? 'border-purple-400 bg-purple-500/30 text-white' 
                        : 'border-white/20 text-gray-300 hover:border-white/40'
                    }`}
                    data-testid="back-9-btn"
                  >
                    <span className="font-medium">Back 9</span>
                    <span className="block text-xs opacity-70">Holes 10-18</span>
                  </button>
                </div>
              </div>
            )}

            {/* Handicaps Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/10">
              <div>
                <span className="text-white font-medium">Use Handicaps</span>
                <p className="text-gray-400 text-xs mt-1">Apply strokes for skins calculation</p>
              </div>
              <button
                onClick={() => handleHandicapsChange(!useHandicaps)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  useHandicaps ? 'bg-purple-500' : 'bg-gray-600'
                }`}
                data-testid="use-handicaps-toggle"
              >
                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  useHandicaps ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Saving indicator */}
            {saving && (
              <div className="flex items-center justify-center gap-2 text-purple-300 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            )}
          </div>
        );
      }

      case 'prizepool': {
        const playerCount = players.length || 2;
        const totalPot = entryFee * playerCount;
        
        return (
          <div className="space-y-5 px-2 py-2">
            {/* Prize Pool Toggle */}
            <div className="text-center">
              <p className="text-white/80 text-sm mb-4 font-medium">Add a Prize Pool?</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handlePrizePoolChange(true)}
                  className={`p-5 rounded-xl border-2 transition-all ${
                    hasPrizePool === true
                      ? 'border-green-400 bg-green-500/30'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                  data-testid="prize-pool-yes"
                >
                  <Trophy className={`w-8 h-8 mx-auto mb-2 ${hasPrizePool === true ? 'text-green-400' : 'text-gray-400'}`} />
                  <span className={`font-bold text-lg ${hasPrizePool === true ? 'text-green-300' : 'text-gray-300'}`}>Yes</span>
                  <p className="text-xs text-gray-400 mt-1">Compete for prizes</p>
                </button>
                <button
                  onClick={() => handlePrizePoolChange(false)}
                  className={`p-5 rounded-xl border-2 transition-all ${
                    hasPrizePool === false
                      ? 'border-blue-400 bg-blue-500/30'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                  data-testid="prize-pool-no"
                >
                  <Users className={`w-8 h-8 mx-auto mb-2 ${hasPrizePool === false ? 'text-blue-400' : 'text-gray-400'}`} />
                  <span className={`font-bold text-lg ${hasPrizePool === false ? 'text-blue-300' : 'text-gray-300'}`}>No</span>
                  <p className="text-xs text-gray-400 mt-1">Just for fun</p>
                </button>
              </div>
            </div>

            {/* Prize Pool Settings */}
            {hasPrizePool === true && (
              <>
                {/* Currency Type */}
                <div>
                  <Label className="text-white/80 text-sm mb-3 block font-medium">Entry Fee Currency</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleEntryFeeTypeChange('wallet')}
                      className={`p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                        entryFeeType === 'wallet'
                          ? 'border-green-400 bg-green-500/30'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                      data-testid="currency-wallet"
                    >
                      <Wallet className={`w-5 h-5 ${entryFeeType === 'wallet' ? 'text-green-400' : 'text-gray-400'}`} />
                      <span className={`font-medium ${entryFeeType === 'wallet' ? 'text-green-300' : 'text-gray-300'}`}>USD</span>
                    </button>
                    <button
                      onClick={() => handleEntryFeeTypeChange('coins')}
                      className={`p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                        entryFeeType === 'coins'
                          ? 'border-amber-400 bg-amber-500/30'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                      data-testid="currency-coins"
                    >
                      <Coins className={`w-5 h-5 ${entryFeeType === 'coins' ? 'text-amber-400' : 'text-gray-400'}`} />
                      <span className={`font-medium ${entryFeeType === 'coins' ? 'text-amber-300' : 'text-gray-300'}`}>Coins</span>
                    </button>
                  </div>
                </div>

                {/* Entry Fee Amount */}
                <div>
                  <Label className="text-white/80 text-sm mb-3 block font-medium">Entry Fee per Player</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {(entryFeeType === 'wallet' 
                      ? [5, 10, 20, 50]
                      : [50, 100, 250, 500]
                    ).map((val) => (
                      <button
                        key={val}
                        onClick={() => handleEntryFeeChange(val)}
                        className={`p-3 rounded-xl border-2 text-sm font-bold transition-all ${
                          entryFee === val
                            ? entryFeeType === 'wallet' 
                              ? 'border-green-400 bg-green-500/30 text-green-300'
                              : 'border-amber-400 bg-amber-500/30 text-amber-300'
                            : 'border-white/20 text-gray-300 hover:border-white/40'
                        }`}
                        data-testid={`entry-fee-${val}`}
                      >
                        {entryFeeType === 'wallet' ? `$${val}` : val}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Prize Pool Preview */}
                <div className={`rounded-xl p-4 text-center ${
                  entryFeeType === 'wallet' ? 'bg-green-500/20' : 'bg-amber-500/20'
                }`}>
                  <p className="text-gray-300 text-sm">Total Pot ({playerCount} players)</p>
                  <p className={`text-3xl font-bold ${entryFeeType === 'wallet' ? 'text-green-300' : 'text-amber-300'}`}>
                    {entryFeeType === 'wallet' ? `$${totalPot}` : `${totalPot.toLocaleString()} coins`}
                  </p>
                </div>
              </>
            )}

            {/* Saving indicator */}
            {saving && (
              <div className="flex items-center justify-center gap-2 text-amber-300 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            )}
          </div>
        );
      }

      case 'start': {
        const allReady = isStepComplete('course') && isStepComplete('tees') && isStepComplete('players') && isStepComplete('prizepool');
        const selectedTeeData = availableTees.find(t => t.name === selectedTee);
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            {allReady ? (
              <>
                <div className="w-14 h-14 mx-auto bg-emerald-500/30 rounded-full flex items-center justify-center mb-3">
                  <Play className="w-12 h-12 text-emerald-400" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-2">Ready to Play!</h4>
                <div className="text-gray-300 text-sm mb-6 space-y-1">
                  <p>{event?.course_name}</p>
                  {selectedTee && (
                    <p className="text-cyan-300 flex items-center justify-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${getTeeColorClass(selectedTee)}`} />
                      {selectedTee} Tees ({selectedTeeData?.total_yards?.toLocaleString() || '---'} yds)
                    </p>
                  )}
                  <p>{players.length} players • {holes} holes</p>
                  {hasPrizePool && (
                    <p className={entryFeeType === 'wallet' ? 'text-green-300' : 'text-amber-300'}>
                      {entryFeeType === 'wallet' ? `$${entryFee}` : `${entryFee} coins`} entry
                    </p>
                  )}
                </div>
                <Button 
                  onClick={handleStartGame}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-10 py-4 text-lg"
                  data-testid="start-skins-game-btn"
                >
                  <Play className="w-6 h-6 mr-2" />
                  Start Skins Game
                </Button>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-6">
                  <Settings className="w-10 h-10 text-gray-400" />
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">Almost Ready!</h4>
                <div className="space-y-3 text-left w-full max-w-xs">
                  {!isStepComplete('course') && (
                    <button 
                      onClick={() => goToStep(steps.findIndex(step => step.id === 'course'))}
                      data-testid="skins-review-course"
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                    >
                      <MapPin className="w-5 h-5" />
                      <span>Select a course</span>
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </button>
                  )}
                  {!isStepComplete('tees') && availableTees.length > 0 && (
                    <button 
                      onClick={() => goToStep(steps.findIndex(step => step.id === 'tees'))}
                      data-testid="skins-review-tees"
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                    >
                      <Flag className="w-5 h-5" />
                      <span>Select a tee box</span>
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </button>
                  )}
                  {!isStepComplete('players') && (
                    <button 
                      onClick={() => goToStep(steps.findIndex(step => step.id === 'players'))}
                      data-testid="skins-review-players"
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                    >
                      <Users className="w-5 h-5" />
                      <span>Add at least 2 players</span>
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </button>
                  )}
                  {!isStepComplete('prizepool') && (
                    <button 
                      onClick={() => goToStep(steps.findIndex(step => step.id === 'prizepool'))}
                      data-testid="skins-review-prize-pool"
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                    >
                      <Trophy className="w-5 h-5" />
                      <span>Choose prize pool option</span>
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return <CarouselSurface id="skins-setup-carousel" label="Skins game setup" open={isOpen} covered={suspended} onExit={onExit} exitLabel="Back to Skins games">
    <div className="carousel-setup-panel">
      <header className="carousel-panel-header">
        <Button variant="ghost" size="icon" data-testid="skins-setup-back" aria-label="Back to Skins games" onClick={onBack || onExit}><ArrowLeft className="w-4 h-4" /></Button>
        <h2 className="carousel-panel-title" data-testid="skins-setup-event-title">{event?.name || 'Skins Game'}</h2>
      </header>
      {(loadError || saveError) && <div role="alert" data-testid="skins-setup-error" className="px-4 text-sm text-red-600">
        {loadError || saveError}{loadError && <Button variant="outline" data-testid="skins-setup-retry" onClick={onRetry}>Refresh game</Button>}
      </div>}
      <div className="flex flex-wrap justify-center gap-1.5 px-3 py-2">
        {steps.map((step, index) => <button type="button" key={step.id} onClick={() => goToStep(index)}
          aria-label={step.title} title={step.title} aria-current={index === currentStep ? 'step' : undefined}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${index === currentStep ? getActiveBg(step.color) : isStepComplete(step.id) ? 'bg-green-500/20 text-green-400' : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-muted)]'}`}
          data-testid={`step-indicator-${step.id}`}>
          {isStepComplete(step.id) && index !== currentStep ? <Check className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
        </button>)}
      </div>
      <p data-testid="skins-setup-step-title" className="text-center text-sm font-medium text-[var(--ps-text-secondary)]">{steps[currentStep].title}</p>
      <LoopingDeck id="skins-setup-deck" items={steps} index={currentStep} onIndexChange={goToStep}
        renderItem={step => <fieldset disabled={saving} data-testid={`skins-content-${step.id}`} className={`carousel-step-body min-w-0 rounded-xl bg-gradient-to-br ${getGradient(step.color)} border ${getBorderColor(step.color)}`}>{renderStepContent(step)}</fieldset>} />
      <CarouselArrows id="skins-setup" previous={() => goToStep(currentStep - 1)} next={() => goToStep(currentStep + 1)} />
    </div>
  </CarouselSurface>;
};

export default SkinsSetupCarousel;
